import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  ArrowUpRight,
  LockKeyhole,
  MessageCircleQuestion,
} from "lucide-react";
import { Justiniano } from "@tribunalea/justiniano";
import {
  CivitaAssistantContext,
  useCivitaAssistant,
} from "./civita-assistant-context.js";

const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";

function pageDefaults(path) {
  return {
    conversationScope: `pagina:${path}:v1`,
    conversationMetadata: { surface: "civita", route: path },
    welcome: "Estoy para ayudarte a entender lo que estás viendo en Civita y pensar el próximo paso con claridad.",
    suggestions: [
      "¿Cómo funciona esta herramienta?",
      "¿Qué información necesito preparar?",
      "Ayudame a decidir por dónde empezar",
    ],
    notebookIds: [],
    requestedSources: [],
    appContext: { route: path },
  };
}

export function CivitaAssistantProvider({ children, getAccessToken, path }) {
  const [registeredConfiguration, setRegisteredConfiguration] = useState(null);
  const [openRequest, setOpenRequest] = useState(null);
  const defaultConfiguration = useMemo(() => pageDefaults(path), [path]);
  const configuration = registeredConfiguration || defaultConfiguration;

  const registerConfiguration = useCallback((nextConfiguration) => {
    setRegisteredConfiguration(nextConfiguration);
    return () => {
      setRegisteredConfiguration((current) => (
        current === nextConfiguration ? null : current
      ));
    };
  }, []);

  const openAssistant = useCallback((view = "chat") => {
    setOpenRequest({ view });
  }, []);
  const handleOpenRequest = useCallback(() => setOpenRequest(null), []);

  const contextValue = useMemo(() => ({
    openAssistant,
    registerConfiguration,
  }), [openAssistant, registerConfiguration]);

  return (
    <CivitaAssistantContext.Provider value={contextValue}>
      {children}
      {path !== "/justiniano" && (
        <Justiniano
          key={configuration.conversationScope}
          appId="civita"
          appName="Civita"
          apiUrl={apiUrl}
          getAccessToken={getAccessToken}
          allowAttachments
          conversationScope={configuration.conversationScope}
          conversationMetadata={configuration.conversationMetadata}
          welcome={configuration.welcome}
          suggestions={configuration.suggestions}
          notebookIds={configuration.notebookIds}
          requestedSources={configuration.requestedSources}
          appContext={configuration.appContext}
          initialDraft={configuration.initialDraft}
          initialView={configuration.initialView}
          openRequest={openRequest}
          onOpenRequestHandled={handleOpenRequest}
        />
      )}
    </CivitaAssistantContext.Provider>
  );
}

export function CivitaAssistantPage({ getAccessToken }) {
  return (
    <main className="civita-justiniano-page">
      <Justiniano
        variant="page"
        appId="civita"
        appName="Civita"
        apiUrl={apiUrl}
        getAccessToken={getAccessToken}
        allowAttachments
        conversationScope="justiniano:v1"
        conversationMetadata={{ surface: "civita", route: "/justiniano" }}
        welcome="Estoy para ayudarte a entender una situación, ordenar tus dudas y pensar el próximo paso con claridad."
        suggestions={[
          "Ayudame a entender un documento",
          "Ayudame a resolver un problema",
          "Ayudame a revisar antes de firmar",
          "Ayudame a seguir mi expediente",
          "Ayudame a preparar una conversación",
          "Ayudame a verificar si es legítimo",
          "Ayudame a encontrar un abogado",
        ]}
        appContext={{ route: "/justiniano", activeEntity: null }}
      />
    </main>
  );
}

export function CivitaAssistantLauncher({
  appContext,
  className = "",
  conversationMetadata,
  conversationScope,
  description,
  eyebrow = "Preguntale a Justiniano",
  initialDraft,
  initialView,
  label = "Abrir Justiniano",
  notebookIds = [],
  requestedSources = [],
  sourceLabel,
  suggestions = [],
  title,
  welcome,
}) {
  const { openAssistant, registerConfiguration } = useCivitaAssistant();
  const serializedConfiguration = JSON.stringify({
    appContext,
    conversationMetadata,
    conversationScope,
    initialDraft,
    initialView,
    notebookIds,
    requestedSources,
    suggestions,
    welcome,
  });
  const configuration = useMemo(() => ({
    appContext,
    conversationMetadata,
    conversationScope,
    initialDraft,
    initialView,
    notebookIds,
    requestedSources,
    suggestions,
    welcome,
  }), [serializedConfiguration]);

  useEffect(() => (
    registerConfiguration(configuration)
  ), [configuration, registerConfiguration]);

  return (
    <section className={`document-assistant is-launcher ${className}`.trim()}>
      <header>
        <div>
          <span className="section-icon"><MessageCircleQuestion size={18} /></span>
          <div>
            <span className="eyebrow">{eyebrow}</span>
            <h2>{title}</h2>
            <p>{description}</p>
          </div>
        </div>
        <div className="assistant-launcher-actions">
          {sourceLabel && (
            <span className="source-lock">
              <LockKeyhole size={14} /> {sourceLabel}
            </span>
          )}
          <button
            className="primary-button assistant-launch-button"
            type="button"
            onClick={() => openAssistant(initialView || "chat")}
          >
            <MessageCircleQuestion size={17} />
            {label}
            <ArrowUpRight size={16} />
          </button>
        </div>
      </header>
    </section>
  );
}
